/**
 * There are many cases where we have objects with symbols only as keys.
 * The built in ways to iterate over these keys are:
 *
 * - Object.getOwnPropertySymbols
 * - Reflect.ownKeys
 *
 * To make this more ergonomic, SymbolMap adds support for using the
 * iterator protocol. This means that you can use spread operator (...),
 * Array.from, for..of, and anything else that uses iterator function.
 *
 * Some examples:
 *
 * ```js
 * Array.from(symbolMap).map(([symbol, value]) => {...});
 * for (const [symbol, value] of symbolMap) {...}
 * ```
 */
export default class SymbolMap<const R extends AnySymbolRecord> {
  [k: symbol]: _Value<R>;

  constructor(...args: R[]) {
    Object.assign(this, ...args);
  }

  static keys<M extends AnySymbolRecord>(symbolMap: M): SymbolMapKey<M>[] {
    return Object.getOwnPropertySymbols(symbolMap) as SymbolMapKey<M>[];
  }

  static values<M extends AnySymbolRecord>(symbolMap: M): _Value<M>[] {
    return SymbolMap.keys(symbolMap).map((k) => symbolMap[k]) as _Value<M>[];
  }

  static entries<M extends AnySymbolRecord>(symbolMap: M): _Entry<M>[] {
    return SymbolMap.keys(symbolMap).map(
      (k) => [k, symbolMap[k]] as unknown as _Entry<M>,
    );
  }

  static fromEntries<K extends symbol, V>(
    entries: [K, V][],
  ): SymbolMap<Record<K, V>> {
    return new SymbolMap(Object.fromEntries(entries));
  }

  static invert<M extends SymbolMap<Record<symbol, PropertyKey>>>(
    symbolMap: M,
  ): _Invert<M> {
    const inverted = {};

    for (const [key, value] of SymbolMap.entries(symbolMap)) {
      inverted[value] = key;
    }

    return inverted as _Invert<M>;
  }

  // @ts-expect-error
  *[Symbol.iterator](): Generator<_Entry<this>, void, unknown> {
    const symbols = SymbolMap.keys(this);

    for (let i = 0; i < symbols.length; i++) {
      const symbol = symbols[i];
      yield [symbol, this[symbol]] as unknown as _Entry<this>;
    }
  }
}

type AnySymbolRecord = Record<symbol, unknown>;

export type SymbolMapKey<M extends AnySymbolRecord> =
  M extends SymbolMap<infer R> ? keyof R : never;

type _Value<M extends AnySymbolRecord> =
  M extends Record<symbol, infer V> ? V : never;
type _Entry<M extends AnySymbolRecord> =
  M extends SymbolMap<infer R> ? { [K in keyof R]: [K, R[K]] }[keyof R] : never;

type _Invert<M extends AnySymbolRecord> =
  M extends SymbolMap<infer R>
    ? { [K in keyof R as R[K] extends PropertyKey ? R[K] : string]: K }
    : never;
